import React from "react";
import {marked} from "marked";
import HelpCircle from "/src/svg/help-circle.svg";
import Icon, {IconSVG} from "../../icon/Icon";
import Tooltip from "../../tooltip/Tooltip";

import {css} from "/src/utils/utils";
import sanitizeDom from "/src/utils/sanitize-dom";
import translate from "/src/utils/translate";

import * as styles from "./label.module.scss";

interface Props {
  ariaHidden?: boolean;
  classes?: string;
  darkMode?: boolean;
  disabled?: boolean;
  id?: string;
  label: string;
  required?: boolean;
  tooltipContent?: string;
  tooltipIcon?: IconSVG;
  tooltipPlainContent?: string;
}

marked.setOptions({sanitize: false});

const renderer = new marked.Renderer();

renderer.link = (href: string, title: string, text: string): string =>
  `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;

// we only want to render links, em, and strong, so we'll ignore the following:
renderer.blockquote = (text: string) => text;
renderer.codespan = (text: string) => text;
renderer.heading = (text: string) => text;
renderer.image = (href: string) => href;
renderer.list = (text: string) => text;
renderer.listitem = (text: string) => text;
renderer.paragraph = (text: string) => text;

export default function Label(props: Props) {
  const {
    ariaHidden = false,
    classes = "",
    darkMode = false,
    disabled = false,
    id = "",
    label = "",
    required = false,
    tooltipContent,
    tooltipIcon = HelpCircle,
    tooltipPlainContent,
  } = props;

  return (
    <div className={css(styles.wrapper, classes)}>
      <span className={styles.label__display}>
        <span
          aria-hidden={ariaHidden}
          id={id}
          className={css(
            styles.label,
            darkMode ? styles.labelDark : "",
            disabled ? styles.labelDisabled : "",
            classes
          )}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizeDom(marked(label || "", {renderer})),
          }}
        />

        {required ? (
          <span aria-hidden="true" tabIndex={-1} className={styles.label}>
            *
          </span>
        ) : null}
      </span>

      {tooltipIcon && tooltipContent != null && (
        <Tooltip
          wrapInAButton
          id={`${id}-helper-tooltip`}
          content={tooltipContent}
          triggerLabel={`${translate("helper tooltip")} - ${
            tooltipPlainContent != null ? tooltipPlainContent : tooltipContent
          }`}
          triggerElem={
            <Icon
              svg={tooltipIcon}
              role="presentation"
              classes={css(
                styles.label__tooltipIcon,
                darkMode ? styles.label__tooltipIconDark : ""
              )}
            />
          }
        />
      )}
    </div>
  );
}
